import { dispatchCustomEvent } from "./customEvent"
import { CUSTOM_EVENT_TOGGLE_THEME } from '@pitayan/gatsby-theme-pitayan/src/constants'

// Perrin themes: System follows the device, Light and Dark are explicit choices
export type ThemeMode = "system" | "light" | "dark"

// The site opens in dark until the visitor picks another theme.
// Keep in step with the inline script in gatsby/node/on-render-body.js
export const DEFAULT_THEME: ThemeMode = "dark"

export const getThemeMode = (): ThemeMode => {
  try {
    const theme = localStorage.theme
    return theme === "light" || theme === "dark" || theme === "system" ? theme : DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

// Matches the browser chrome to the page ground (Perrin bg) for the current theme
export const syncThemeColor = (): void => {
  const bg = getComputedStyle(document.documentElement).getPropertyValue("--bg").trim()
  document.querySelectorAll('meta[name="theme-color"]').forEach(meta => meta.setAttribute("content", bg))
}

export const checkIfDarkMode = (): boolean =>
  typeof document !== "undefined" &&
  document.documentElement.getAttribute("data-theme") === "dark"

// Mirrors Perrin.applyTheme: sets data-theme and the dark class, and updates the browser chrome colour
export const applyTheme = (mode: ThemeMode): void => {
  const root = document.documentElement
  const isDark =
    mode === "dark" ||
    (mode === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches)

  root.setAttribute("data-theme", isDark ? "dark" : "light")
  root.classList.toggle("dark", isDark)
  root.style.colorScheme = isDark ? "dark" : "light"
  syncThemeColor()

  // System is saved too: with a dark default, an empty setting would mean dark
  try {
    localStorage.theme = mode
  } catch {
    // Storage can be blocked; the theme still applies for this page
  }

  dispatchCustomEvent(CUSTOM_EVENT_TOGGLE_THEME, { theme: mode })
}
